import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { type FindingResultPaginatedResponse } from '../../utils/api-client/api-schemas.ts'

type FindingType = 'triage' | 'fix' | 'sca'

type UseActivityResultSelectionInput = {
  results: FindingResultPaginatedResponse | undefined
  findingType: FindingType
}

type UseActivityResultSelectionOutput = {
  activeResultId: string | undefined
  isLive: boolean
}

export const useActivityResultSelection = ({
  results,
  findingType,
}: UseActivityResultSelectionInput): UseActivityResultSelectionOutput => {
  return useMemo(() => selectActivityResult(results, findingType), [results, findingType])
}

/**
 * Selects which finding result to stream activity for, given a finding type.
 *
 * Priority:
 * 1. An in-progress result (no `outcome`) — returned with `isLive: true`
 *    so the activity tab can show a live stream.
 * 2. The most recently completed result (by `completed` timestamp) — returned
 *    with `isLive: false` so the activity tab shows the historical feed.
 * 3. `undefined` if no results of the given type exist.
 */
export const selectActivityResult = (
  results: FindingResultPaginatedResponse | undefined,
  findingType: FindingType
): UseActivityResultSelectionOutput => {
  const items = results?._embedded?.items
  if (!items) {
    return { activeResultId: undefined, isLive: false }
  }

  const typed = items.filter(r => r.type === findingType)

  const inProgress = typed.find(r => !r.outcome)
  if (inProgress) {
    return { activeResultId: inProgress.id, isLive: true }
  }

  const completed = typed
    .filter(r => Boolean(r.completed))
    .sort((a, b) => {
      const aTime = a.completed ? DateTime.fromISO(a.completed).toMillis() : 0
      const bTime = b.completed ? DateTime.fromISO(b.completed).toMillis() : 0
      return bTime - aTime
    })

  const mostRecent = completed[0]
  if (mostRecent) {
    return { activeResultId: mostRecent.id, isLive: false }
  }

  return { activeResultId: undefined, isLive: false }
}
